import { RecurrentDestinationInterface } from '../interfaces'
import { ListResponse } from '../interfaces/Shipment/ListResponseDto'

/**
 * Caché de la libreta de destinatarios, por cuenta y sólo en memoria.
 *
 * Vive aquí y no dentro de `AccountServices` a propósito: quien tiene que
 * vaciarla al cerrar sesión es `LogoutTimer` y el encabezado, y esos dos se
 * prueban aislados justamente para no arrastrar axios ni el resto de los
 * servicios. Este módulo no importa nada que haga red.
 *
 * Ni Redux ni almacenamiento: redux-persist envuelve el reducer raíz completo,
 * así que un slice escribiría datos personales de destinatarios en
 * localStorage. Un Map de módulo muere con la página y se vacía al salir.
 */
export const FAVORITE_DESTINATIONS_TTL = 5 * 60 * 1000

export interface FavoriteDestinationsCacheEntry {
  expiresAt: number
  promise: Promise<ListResponse<RecurrentDestinationInterface[]>>
}

const favoriteDestinationsCache = new Map<
  string,
  FavoriteDestinationsCacheEntry
>()

export const getCachedFavoriteDestinations = (
  accountId: string
): FavoriteDestinationsCacheEntry | undefined => {
  const entry = favoriteDestinationsCache.get(accountId)
  return !!entry && entry.expiresAt > Date.now() ? entry : undefined
}

export const setCachedFavoriteDestinations = (
  accountId: string,
  promise: Promise<ListResponse<RecurrentDestinationInterface[]>>
): void => {
  const now = Date.now()

  // Barrido al insertar: la expiración sólo se mira al leer, así que un usuario
  // que pase por muchas cuentas se quedaría con todas las libretas en memoria.
  favoriteDestinationsCache.forEach((entry, key) => {
    if (entry.expiresAt <= now) {
      favoriteDestinationsCache.delete(key)
    }
  })

  favoriteDestinationsCache.set(accountId, {
    expiresAt: now + FAVORITE_DESTINATIONS_TTL,
    promise,
  })
}

/**
 * Tira libretas cacheadas. Sin argumento vacía todas: eso es lo que hay que
 * llamar al cerrar sesión, porque la libreta son datos de terceros y el equipo
 * comparte equipos.
 */
export const invalidateFavoriteDestinationsCache = (accountId?: string) => {
  if (accountId === undefined) {
    favoriteDestinationsCache.clear()
    return
  }

  favoriteDestinationsCache.delete(accountId)
}

/** Sólo para las pruebas. */
export const favoriteDestinationsCacheSize = (): number =>
  favoriteDestinationsCache.size
